import React, { useState, useEffect, useRef } from 'react';
import { X, Trash2, Sparkles, AlertTriangle } from 'lucide-react';
import { FoodAutocomplete, QuantityPicker, pairedName, SourceTag, SCOOP_SIZES } from './FoodPicker';
import AddFoodModal from './AddFoodModal';
import { createRecipe, updateRecipe, checkIngredientLines, parseRecipeIngredients, getFoodClassification, getScoopWeightSuggestion, getFoodScoopMlSuggestion } from '../api';

// Create/edit a chef recipe — name, Bord/MD label, portion count (recipeYield), a free-text
// "bereiding", and its ingredients. The primary way to enter ingredients is pasting a whole list
// (one per line, always in grams — "300 g kipfilet" or "kipfilet 300 g") into the textarea below
// and letting the server (POST /api/nutrition/recipes/parse-ingredients) split + match each line
// against NEVO via the same alias search the eetmeter uses, falling back to Claude when that
// direct match is weak or absent. Every resulting row — parsed or not yet matched — still goes
// through the exact same FoodAutocomplete/QuantityPicker (FoodPicker.js) the old one-by-one flow
// used, so the chef can always correct a wrong or missing match before saving. Macros and
// vegetable grams are never computed here — the server always derives them fresh from the saved
// ingredients (server.staging.js's computeRecipeMacros) and hands back the result after Save.
const CHEF_MD_LABELS = ['MD-4', 'MD-3', 'MD-2', 'MD-1', 'MD', 'MD+1', 'MD+2', 'MD+3', 'MD+4'];
// Per-ingredient role — groundwork for the player "meal of the day" scoop-logging feature
// (server.staging.js's nutrition_meal_scoop_logs): which scoop station this ingredient belongs
// to. Which roles share a scoop station is set below (scoopGroups), not per-ingredient.
const ROLE_OPTIONS = [
  { key: 'protein', label: 'Eiwitbron' },
  { key: 'carbs', label: 'Koolhydraatbron' },
  { key: 'vegetable', label: 'Groentebron' },
  { key: 'other', label: 'Overig' },
];
// Opscheplepel-indeling: the chef gives each of the 4 roles a group letter — roles sharing a
// letter are scooped together as one station, a role alone in its group gets its own station.
// Simpler and less error-prone than a matrix of "are X and Y together?" checkboxes (which could
// produce an inconsistent/non-transitive grouping); a shared label always yields a valid
// partition by construction, so no extra validation is needed before sending it to the server.
// Each resulting group can also optionally carry a chef-defined unit_name (e.g. "asparagus
// spear") — kept in a separate map, keyed by the SAME group label, so it survives the chef
// reshuffling which roles share a label without losing what they typed.
const GROUP_LABELS = ['1', '2', '3', '4'];
const DEFAULT_ROLE_GROUP_LABELS = { protein: '1', carbs: '2', vegetable: '3', other: '4' }; // all apart
// SCOOP_SIZES (FoodPicker.js) — the kitchen's three fixed physical scoops, shown to the chef by
// name (kleine groene lepel / soeplepel / grote blauwe lepel), ml in parentheses for reference.
// A plain (non-unit) scoop group's real weight (scoop_grams) is set per recipe+group below, and
// auto-fills from any other recipe/scoop-size ever weighed for this exact set of products
// (server.staging.js's nutrition_scoop_density, via getScoopWeightSuggestion) — missing that,
// scoop_ml is used directly as grams (computeGroupPerCount). Which scoop a group uses is chosen
// here, per recipe, building up "which scoop goes with which dish" over time — see also the
// admin overview (ChefWeekPlanner.js) for the same across many recipes.

// {protein:'1', carbs:'1', vegetable:'2', other:'3'}, {'1':null,'2':'asparagus spear'}, {'1':237,'2':null}, {'1':150,'2':null}
//   -> [{roles:['protein','carbs'], unit_name:null, scoop_ml:237, scoop_grams:150}, {roles:['vegetable'], unit_name:'asparagus spear', scoop_ml:null, scoop_grams:null}]
function deriveScoopGroups(roleGroupLabels, groupUnitNames, groupScoopMl, groupScoopGrams) {
  const byLabel = {};
  ROLE_OPTIONS.forEach(r => {
    const l = roleGroupLabels[r.key];
    (byLabel[l] = byLabel[l] || []).push(r.key);
  });
  return Object.entries(byLabel).map(([label, roles]) => ({
    roles, unit_name: groupUnitNames[label] || null, scoop_ml: groupScoopMl[label] || null,
    scoop_grams: groupUnitNames[label] == null ? (groupScoopGrams[label] || null) : null,
  }));
}
// [{roles:['protein','carbs'],unit_name:null,scoop_ml:237,scoop_grams:150},{roles:['vegetable'],unit_name:'asparagus spear',scoop_ml:null,scoop_grams:null}]
//   -> { roleGroupLabels: {...}, groupUnitNames: {'1':null, '2':'asparagus spear'}, groupScoopMl: {'1':237, '2':null}, groupScoopGrams: {'1':150, '2':null} }
function groupStateFromScoopGroups(scoopGroups) {
  const roleGroupLabels = {};
  const groupUnitNames = {};
  const groupScoopMl = {};
  const groupScoopGrams = {};
  (scoopGroups || []).forEach((group, i) => {
    const groupLabel = GROUP_LABELS[i] || String(i + 1);
    (group.roles || []).forEach(role => { roleGroupLabels[role] = groupLabel; });
    groupUnitNames[groupLabel] = group.unit_name || null;
    groupScoopMl[groupLabel] = group.scoop_ml || null;
    groupScoopGrams[groupLabel] = group.scoop_grams != null ? group.scoop_grams : null;
  });
  return { roleGroupLabels: { ...DEFAULT_ROLE_GROUP_LABELS, ...roleGroupLabels }, groupUnitNames, groupScoopMl, groupScoopGrams };
}

const inputStyle = { padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box', outline: 'none' };
const label = { fontSize: 11, color: 'var(--text-muted)', marginBottom: 3, display: 'block' };
const btn = (active, color = '#3b82f6') => ({
  padding: '7px 12px', borderRadius: 8, border: `1px solid ${active ? color : 'var(--border-strong)'}`,
  background: active ? color : 'transparent', color: active ? '#fff' : 'var(--text-secondary)',
  fontSize: 12, fontWeight: active ? 600 : 400, cursor: 'pointer', fontFamily: 'inherit',
});
const ghostBtn = { padding: '3px 8px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' };

// 'alias' (confident direct match) and 'manual' (the chef picked it themselves) need no flag —
// 'ai' and 'alias_weak' mean the server wasn't sure, so the chef should double-check.
function MatchBadge({ matchedVia }) {
  if (matchedVia !== 'ai' && matchedVia !== 'alias_weak') return null;
  const text = matchedVia === 'ai' ? 'AI-match — controleer' : 'Onzekere match — controleer';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 700, color: '#b45309', background: 'rgba(245,158,11,0.15)', padding: '2px 6px', borderRadius: 6, marginLeft: 6 }}>
      <Sparkles size={10} />{text}
    </span>
  );
}

export default function RecipeEditor({ initial, defaultMdLabel, onSaved, onCancel }) {
  const [name, setName] = useState(initial?.name || '');
  const [mdLabel, setMdLabel] = useState(initial?.md_label || defaultMdLabel || 'MD');
  const [yieldCount, setYieldCount] = useState(initial?.recipe_yield ? String(initial.recipe_yield) : '4');
  const [preparation, setPreparation] = useState(initial?.preparation || '');
  const [materials, setMaterials] = useState(initial?.materials || '');
  const [notesAllergens, setNotesAllergens] = useState(initial?.notes_allergens || '');
  const [{ roleGroupLabels, groupUnitNames, groupScoopMl, groupScoopGrams }, setGroupState] = useState(() => groupStateFromScoopGroups(initial?.scoop_groups));
  const setRoleGroupLabels = (updater) => setGroupState(s => ({ ...s, roleGroupLabels: updater(s.roleGroupLabels) }));
  const setGroupUnitNames = (updater) => setGroupState(s => ({ ...s, groupUnitNames: updater(s.groupUnitNames) }));
  const setGroupScoopMl = (updater) => setGroupState(s => ({ ...s, groupScoopMl: updater(s.groupScoopMl) }));
  const setGroupScoopGrams = (updater) => setGroupState(s => ({ ...s, groupScoopGrams: updater(s.groupScoopGrams) }));
  // What this recipe's groups ALREADY had saved, by label, at open time — so picking the exact
  // size a group already has its own saved weight for shows that value directly instead of
  // re-resolving it (same distinction GroupScoopEditor in ChefWeekPlanner.js makes).
  const initialScoopRef = useRef(groupStateFromScoopGroups(initial?.scoop_groups));
  // Existing ingredients (edit mode) arrive as {id, food_id, name, name_en, source,
  // amount_grams, role} — no grams_per_portion, so a reconstructed minimal food object is passed
  // to QuantityPicker. That's fine: for NEVO ingredients (the vast majority) it fetches real
  // Portie-online measures itself regardless of what's passed in; grams_per_portion is only a
  // fallback for barcode/photo-scanned foods with no named measures.
  const [ingredients, setIngredients] = useState(() =>
    (initial?.ingredients || []).map(ing => ({
      key: `existing-${ing.id}`,
      food: { id: ing.food_id, name: ing.name, name_en: ing.name_en, source: ing.source },
      amount_grams: ing.amount_grams,
      role: ing.role || 'other',
      matchedVia: null,
      raw: null,
      quantityNote: null,
    }))
  );
  // Existing ingredients (edit mode) never got their raw-vs-cooked check run when the recipe was
  // first saved (that field is new) — fetch it for each once on open so re-editing an older
  // recipe surfaces the same note a fresh add/match would, without the chef re-adding anything.
  useEffect(() => {
    (initial?.ingredients || []).forEach(ing => {
      const key = `existing-${ing.id}`;
      getFoodClassification(ing.food_id).then(cls => {
        if (cls.quantity_state_note) setIngredients(list => list.map(x => x.key === key ? { ...x, quantityNote: cls.quantity_state_note } : x));
      }).catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // The chef's own free-text ingredient list exactly as pasted, kept permanently alongside the
  // structured ingredients it produced (server.staging.js's written_recipe_text) — unlike
  // bulkText below (the working scratch copy, cleared once its lines are matched to NEVO), this
  // is never cleared: every "Controleer hoeveelheden" appends that round's pasted text here, so
  // it survives the whole edit session and future re-opens of this recipe.
  const [writtenText, setWrittenText] = useState(initial?.written_recipe_text || '');
  const [bulkText, setBulkText] = useState('');
  // Step 1 of "Ingrediënten herkennen": the chef's pasted text is split into lines and each gets
  // a gram amount extracted (server.staging.js's parseIngredientLine, no NEVO/AI involved yet) —
  // kept here, separate from `ingredients`, and editable so a line missing an amount can be
  // fixed BEFORE anything is matched to NEVO. bulkText itself is deliberately left untouched
  // while this is in progress, so the chef can always see exactly what they typed/pasted.
  const [checkedLines, setCheckedLines] = useState(null); // null | [{raw, amount_grams, name, error}]
  const [checking, setChecking] = useState(false);
  const [matching, setMatching] = useState(false);
  const [openCorrection, setOpenCorrection] = useState(null); // key of the row whose FoodAutocomplete is expanded
  // null | 'new' (the standalone "Nieuw product toevoegen…" button) | an ingredient row's key
  // (opened from that specific unmatched row) — either way AddFoodModal's onAdded below
  // routes the result to the right place, so a chef never has to add a product loose and then
  // go hunt it back down in FoodAutocomplete to attach it to the line that needed it.
  const [addFoodTarget, setAddFoodTarget] = useState(null);
  // Set after a successful save when the server flagged a role/database-classification mismatch
  // (e.g. an ingredient marked "Overig" while the database has it tagged as a vegetable) — the
  // recipe is already saved at this point; this is purely informational, shown once before
  // closing so the chef notices and can go fix the role(s) on a later edit.
  const [roleWarnings, setRoleWarnings] = useState(null); // {warnings, saved} | null
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Suggests a role straight from the food's existing database classification (the same
  // veg-species/protein-type data the role-mismatch check already compares against) instead of
  // always defaulting to 'other' — the chef can still override it, and an override that disagrees
  // with this same classification is exactly what that mismatch warning flags on save. The same
  // call also carries quantity_state_note — server.staging.js's raw-vs-cooked/fried check (rice,
  // pasta, meat, ... have very different macros per 100g depending on which NEVO variant is
  // selected) — so adding or swapping an ingredient one at a time gets this for free too, not
  // just the bulk-paste flow (parse-ingredients returns its own copy of the same field).
  const fetchFoodMeta = async (food) => {
    try {
      const cls = await getFoodClassification(food.id);
      return { role: cls.expected_role || 'other', quantityNote: cls.quantity_state_note || null };
    } catch { return { role: 'other', quantityNote: null }; }
  };

  const addIngredient = async (food) => {
    const key = `new-${food.id}-${Date.now()}`;
    setIngredients(list => [...list, { key, food, amount_grams: null, role: 'other', matchedVia: null, raw: null, quantityNote: null }]);
    const { role, quantityNote } = await fetchFoodMeta(food);
    setIngredients(list => list.map(x => x.key === key ? { ...x, role, quantityNote } : x));
    // This food may already have a known scoop size from elsewhere (nutrition_food_scoop_ml) —
    // auto-pick it for whichever group this role currently falls under, same as if the chef had
    // clicked that size themselves (which also cascades into the weight auto-fill below). Never
    // overrides a size this group already has chosen for THIS recipe.
    const groupLabel = roleGroupLabels[role];
    if (groupScoopMl[groupLabel] == null && groupUnitNames[groupLabel] == null) {
      try {
        const { scoop_ml } = await getFoodScoopMlSuggestion(food.id);
        if (scoop_ml != null) await applyScoopMl(groupLabel, scoop_ml, rolesInGroup(groupLabel));
      } catch { /* never used with a scoop before — leave unset, chef picks manually */ }
    }
  };
  const removeIngredient = (key) => setIngredients(list => list.filter(x => x.key !== key));
  // A scoop group's identity for weight reuse — mirrors server.staging.js's
  // foodSetKeyForGroup: the exact set of NEVO food_ids currently in these roles, order-
  // independent. Computed from `ingredients` directly (no server round-trip needed) since this
  // editor already holds every ingredient's role + food.
  const foodIdsForRoles = (roles) => [...new Set(ingredients.filter(x => roles.includes(x.role) && x.food).map(x => x.food.id))];
  const rolesInGroup = (groupLabel) => ROLE_OPTIONS.filter(r => roleGroupLabels[r.key] === groupLabel).map(r => r.key);
  // Sets a group's scoop size and auto-fills its weight from whatever was ever recorded for
  // this exact product set at that size, in this or any other recipe (getScoopWeightSuggestion)
  // — a real auto-fill, not just a prefilled suggestion. Shared by the scoop-size buttons
  // (pickScoopMl below, which also handles un-picking) and by addIngredient's own auto-pick.
  const applyScoopMl = async (groupLabel, ml, roles) => {
    setGroupScoopMl(g => ({ ...g, [groupLabel]: ml }));
    const orig = initialScoopRef.current.groupScoopMl[groupLabel];
    const origGrams = initialScoopRef.current.groupScoopGrams[groupLabel];
    if (orig === ml && origGrams != null) {
      setGroupScoopGrams(g => ({ ...g, [groupLabel]: origGrams }));
      return;
    }
    setGroupScoopGrams(g => ({ ...g, [groupLabel]: null }));
    const foodIds = foodIdsForRoles(roles);
    if (foodIds.length) {
      try {
        const { grams } = await getScoopWeightSuggestion(foodIds, ml);
        if (grams != null) setGroupScoopGrams(g => ({ ...g, [groupLabel]: grams }));
      } catch { /* never weighed for this exact product set yet — leave blank */ }
    }
  };
  // Pick (or un-pick) a group's physical scoop size via the buttons — re-picking the size this
  // group ALREADY had saved (at open time) restores that saved weight directly, same as
  // applyScoopMl; clicking the already-selected size clears it instead.
  const pickScoopMl = async (groupLabel, ml, roles) => {
    if (groupScoopMl[groupLabel] === ml) {
      setGroupScoopMl(g => ({ ...g, [groupLabel]: null }));
      setGroupScoopGrams(g => ({ ...g, [groupLabel]: null }));
      return;
    }
    await applyScoopMl(groupLabel, ml, roles);
  };
  const setAmount = (key, grams) => setIngredients(list => list.map(x => x.key === key ? { ...x, amount_grams: grams } : x));
  const setRole = (key, role) => setIngredients(list => list.map(x => x.key === key ? { ...x, role } : x));
  const replaceFood = async (key, food) => {
    setIngredients(list => list.map(x => x.key === key ? { ...x, food, matchedVia: 'manual', quantityNote: null } : x));
    setOpenCorrection(null);
    const { role, quantityNote } = await fetchFoodMeta(food);
    setIngredients(list => list.map(x => x.key === key ? { ...x, role, quantityNote } : x));
  };
  // The "use converted amount" action on a raw-vs-cooked note: when the server found a confident
  // paired food (opposite state, e.g. "Rijst witte rauw" -> "Rijst witte gekookt"), switch this
  // row to it and apply the factor to the amount in the same step — refetches quantity_state_note
  // for the new food too (it flips state, so the note itself should flip, not just disappear).
  const applyQuantityConversion = async (key, note, convertedGrams) => {
    if (!note.paired_food) return;
    const newFood = { id: note.paired_food.id, name: note.paired_food.name, source: 'nevo' };
    setIngredients(list => list.map(x => x.key === key ? { ...x, food: newFood, amount_grams: Math.round(convertedGrams), quantityNote: null } : x));
    const { quantityNote } = await fetchFoodMeta(newFood);
    setIngredients(list => list.map(x => x.key === key ? { ...x, quantityNote } : x));
  };

  // Step 1: parse the pasted text into lines + gram amounts only — no NEVO/AI yet. bulkText stays
  // exactly as typed so the chef keeps seeing their own input while checking/fixing amounts.
  const checkBulk = async () => {
    setError('');
    if (!bulkText.trim()) return;
    setChecking(true);
    try {
      const { lines } = await checkIngredientLines(bulkText);
      setCheckedLines(lines.map(l => ({ raw: l.raw, name: l.name, amount_grams: l.amount_grams })));
      setWrittenText(prev => (prev.trim() ? `${prev.trim()}\n${bulkText.trim()}` : bulkText.trim()));
    } catch (e) {
      setError(e.message || 'Controleren van ingrediënten mislukt.');
    } finally { setChecking(false); }
  };
  const setCheckedAmount = (i, grams) => setCheckedLines(list => list.map((l, idx) => idx === i ? { ...l, amount_grams: grams } : l));
  const removeCheckedLine = (i) => setCheckedLines(list => list.filter((_, idx) => idx !== i));

  // Step 2: only reachable once every checked line has a gram amount — match each against NEVO
  // (alias search + AI fallback, unchanged) and add the results as real ingredient rows.
  const matchBulk = async () => {
    setError('');
    if (!checkedLines || !checkedLines.length || checkedLines.some(l => !(l.amount_grams > 0))) return;
    setMatching(true);
    try {
      const { lines } = await parseRecipeIngredients(checkedLines);
      const newRows = lines.map((l, i) => ({
        key: `parsed-${Date.now()}-${i}`,
        food: l.food || null,
        amount_grams: l.amount_grams,
        role: l.food ? (l.expected_role || 'other') : 'other',
        matchedVia: l.food ? l.matched_via : null,
        raw: l.raw,
        quantityNote: l.quantity_state_note || null,
      }));
      setIngredients(list => [...list, ...newRows]);
      setBulkText('');
      setCheckedLines(null);
    } catch (e) {
      setError(e.message || 'Herkennen van ingrediënten mislukt.');
    } finally { setMatching(false); }
  };

  const save = async () => {
    setError('');
    if (!name.trim()) return setError('Name is required.');
    const yieldNum = Number(yieldCount);
    if (!(Number.isInteger(yieldNum) && yieldNum > 0)) return setError('Portions must be a whole number greater than 0.');
    if (!ingredients.length) return setError('Add at least one ingredient.');
    if (ingredients.some(x => !x.food)) return setError('Koppel eerst elk ingrediënt aan een NEVO-item voordat je opslaat.');
    const payloadIngredients = ingredients.map(x => ({ food_id: x.food.id, amount_grams: x.amount_grams, role: x.role }));
    if (payloadIngredients.some(x => !(x.amount_grams > 0))) return setError('Every ingredient needs an amount.');

    setSaving(true);
    try {
      const data = {
        name: name.trim(), md_label: mdLabel, recipe_yield: yieldNum, preparation: preparation.trim(),
        materials: materials.trim(), notes_allergens: notesAllergens.trim(), written_recipe_text: writtenText.trim(),
        scoop_groups: deriveScoopGroups(roleGroupLabels, groupUnitNames, groupScoopMl, groupScoopGrams),
        ingredients: payloadIngredients,
      };
      const saved = initial ? await updateRecipe(initial.id, data) : await createRecipe(data);
      // An admin-approved mismatch is a deliberate, already-reviewed exception — no need to nag
      // the chef about it again on every save, only genuinely unapproved ones.
      const unapproved = (saved.role_warnings || []).filter(w => !w.approved);
      if (unapproved.length) {
        setRoleWarnings({ warnings: unapproved, saved });
      } else {
        onSaved(saved);
      }
    } catch (e) {
      setError(e.message || 'Save failed.');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onCancel}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 16, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg-card)' }}>
          <span style={{ fontSize: 14, fontWeight: 700 }}>{initial ? 'Edit recipe' : 'New recipe'}</span>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}><X size={18} /></button>
        </div>

        <div style={{ padding: 16 }}>
          {(writtenText || !initial) && (
            <div style={{ marginBottom: 14 }}>
              <span style={label}>Origineel geschreven recept</span>
              <textarea value={writtenText} onChange={e => setWrittenText(e.target.value)}
                style={{ ...inputStyle, minHeight: 60, resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }}
                placeholder="Wordt automatisch gevuld zodra je hieronder een ingrediëntenlijst plakt en controleert — ook los hiervan zelf te schrijven of aan te passen." />
            </div>
          )}
          <span style={label}>Name</span>
          <input value={name} onChange={e => setName(e.target.value)} style={inputStyle} placeholder="e.g. Kip, courgette en rijst" autoFocus />

          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <div style={{ flex: 1 }}>
              <span style={label}>Bord / MD</span>
              <select value={mdLabel} onChange={e => setMdLabel(e.target.value)} style={inputStyle}>
                {CHEF_MD_LABELS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div style={{ width: 100 }}>
              <span style={label}>Portions</span>
              <input type="number" min="1" step="1" value={yieldCount} onChange={e => setYieldCount(e.target.value)} style={inputStyle} />
            </div>
          </div>

          <div style={{ marginTop: 10 }}>
            <span style={label}>Opscheplepel-indeling</span>
            <div style={{ fontSize: 10, color: 'var(--text-dimmed)', marginBottom: 6 }}>
              Geef bronnen dezelfde groep als ze samen (één opscheplepel) geserveerd worden — een bron alleen in zijn groep krijgt een eigen opscheplepel.
            </div>
            {ROLE_OPTIONS.map(r => (
              <div key={r.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{r.label}</span>
                <select value={roleGroupLabels[r.key]} onChange={e => setRoleGroupLabels(g => ({ ...g, [r.key]: e.target.value }))}
                  style={{ ...inputStyle, width: 100, padding: '5px 8px' }}>
                  {GROUP_LABELS.map(l => <option key={l} value={l}>Groep {l}</option>)}
                </select>
              </div>
            ))}

            {(() => {
              const labelToRoles = {};
              ROLE_OPTIONS.forEach(r => { const l = roleGroupLabels[r.key]; (labelToRoles[l] = labelToRoles[l] || []).push(r.key); });
              return (
                <div style={{ marginTop: 8, background: 'var(--bg-app)', borderRadius: 8, padding: 8 }}>
                  {Object.entries(labelToRoles).sort(([a], [b]) => a.localeCompare(b)).map(([groupLabel, roles]) => {
                    const roleNames = roles.map(role => ROLE_OPTIONS.find(r => r.key === role)?.label).join(' + ');
                    const usesUnit = groupUnitNames[groupLabel] != null;
                    return (
                      <div key={groupLabel} style={{ marginBottom: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                          <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{roleNames}</span>
                          <label style={{ fontSize: 10, color: 'var(--text-dimmed)', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                            <input type="checkbox" checked={usesUnit}
                              onChange={e => setGroupUnitNames(g => ({ ...g, [groupLabel]: e.target.checked ? '' : null }))} />
                            Log per vaste eenheid
                          </label>
                        </div>
                        {usesUnit ? (
                          <input value={groupUnitNames[groupLabel] || ''} onChange={e => setGroupUnitNames(g => ({ ...g, [groupLabel]: e.target.value }))}
                            style={{ ...inputStyle, marginTop: 4 }} placeholder='Eenheidsnaam in het Engels, bv. "steak", "sweet potato"' />
                        ) : (
                          <div style={{ marginTop: 4 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 10, color: 'var(--text-dimmed)' }}>Opscheplepel:</span>
                              {SCOOP_SIZES.map(({ ml, name }) => (
                                <button key={ml} type="button" style={{ ...btn(groupScoopMl[groupLabel] === ml), padding: '3px 8px', fontSize: 11 }}
                                  onClick={() => pickScoopMl(groupLabel, ml, roles)}>
                                  {name} <span style={{ opacity: 0.75 }}>({ml} ml)</span>
                                </button>
                              ))}
                            </div>
                            {groupScoopMl[groupLabel] && (
                              <div style={{ marginTop: 5, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <input type="number" min="0" step="0.1" placeholder={`nog niet gewogen (${groupScoopMl[groupLabel]}g)`}
                                  value={groupScoopGrams[groupLabel] != null ? String(groupScoopGrams[groupLabel]) : ''}
                                  onChange={e => setGroupScoopGrams(g => ({ ...g, [groupLabel]: e.target.value.trim() === '' ? null : Number(e.target.value) }))}
                                  style={{ ...inputStyle, maxWidth: 90, padding: '4px 8px' }} />
                                <span style={{ fontSize: 10, color: 'var(--text-dimmed)' }}>g — echt gewicht</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          <div style={{ marginTop: 14 }}>
            <span style={label}>Ingrediëntenlijst plakken</span>
            <textarea value={bulkText} onChange={e => { setBulkText(e.target.value); setCheckedLines(null); }}
              style={{ ...inputStyle, minHeight: 70, resize: 'vertical', fontFamily: 'monospace' }}
              placeholder={'Eén ingrediënt per regel, altijd in gram, bv.\n300 g kipfilet\nrijst 200 g'} />
            {checkedLines === null && (
              <button onClick={checkBulk} disabled={checking || !bulkText.trim()} style={{ ...btn(true), marginTop: 6 }}>
                {checking ? 'Controleren…' : 'Controleer hoeveelheden'}
              </button>
            )}
            {checkedLines !== null && (() => {
              const missing = checkedLines.filter(l => !(l.amount_grams > 0));
              return (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginBottom: 6 }}>
                    {missing.length
                      ? 'Vul bij elke regel eerst een hoeveelheid in gram in — pas daarna worden ze aan NEVO gekoppeld.'
                      : 'Alle regels hebben een hoeveelheid. Klaar om aan NEVO te koppelen.'}
                  </div>
                  {checkedLines.map((l, i) => {
                    const hasAmount = l.amount_grams > 0;
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', marginBottom: 4, borderRadius: 6, background: 'var(--bg-app)', border: `1px solid ${hasAmount ? 'transparent' : '#ef4444'}` }}>
                        <span style={{ fontSize: 12, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.raw}>{l.raw}</span>
                        <input type="number" min="0" step="1" value={l.amount_grams ?? ''}
                          onChange={e => setCheckedAmount(i, e.target.value ? Number(e.target.value) : null)}
                          style={{ ...inputStyle, width: 70, padding: '4px 6px' }} placeholder="gram" />
                        <button onClick={() => removeCheckedLine(i)} style={{ ...btn(false, '#ef4444'), padding: '3px 6px' }} title="Remove"><Trash2 size={11} /></button>
                        {!hasAmount && <span style={{ fontSize: 10, color: '#ef4444' }}>hoeveelheid ontbreekt</span>}
                      </div>
                    );
                  })}
                  <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                    <button onClick={matchBulk} disabled={matching || !checkedLines.length || missing.length > 0} style={btn(true)}>
                      {matching ? 'Herkennen…' : 'Herken in NEVO'}
                    </button>
                    <button onClick={() => setCheckedLines(null)} style={btn(false)}>Annuleren</button>
                  </div>
                </div>
              );
            })()}
          </div>

          <div style={{ marginTop: 14 }}>
            <span style={label}>Ingredients</span>
            {ingredients.map(x => {
              const needsAttention = !x.food || x.matchedVia === 'ai' || x.matchedVia === 'alias_weak';
              return (
                <div key={x.key} style={{ background: 'var(--bg-app)', borderRadius: 8, padding: 8, marginTop: 6, border: `1px solid ${!x.food ? '#ef4444' : needsAttention ? '#f59e0b' : 'transparent'}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, gap: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>
                      {x.food ? <>{pairedName(x.food)}<SourceTag source={x.food.source} /></> : <em style={{ color: 'var(--text-muted)', fontWeight: 400 }}>{x.raw || 'Onbekend ingrediënt'}</em>}
                      <MatchBadge matchedVia={x.matchedVia} />
                    </span>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      {x.food && <button onClick={() => setOpenCorrection(o => o === x.key ? null : x.key)} style={ghostBtn}>Wijzig</button>}
                      <button onClick={() => removeIngredient(x.key)} style={{ ...btn(false, '#ef4444'), padding: '3px 6px' }} title="Remove"><Trash2 size={11} /></button>
                    </div>
                  </div>
                  {x.food ? (
                    // Keyed on the food id so swapping to a different NEVO item (replaceFood, or
                    // the raw-vs-cooked "use converted amount" button) forces a fresh mount that
                    // picks up the new amount_grams as its actual initial value — without this,
                    // QuantityPicker/PlainGramsPicker keeps its OWN internal grams state from the
                    // previous food (initialGrams is only ever read once, on mount) and its
                    // change-effect then pushes that stale number straight back up over whatever
                    // was just set, which is why the converted amount never visibly took effect.
                    <QuantityPicker key={`${x.key}-${x.food.id}`} food={x.food} initialGrams={x.amount_grams} onChange={g => setAmount(x.key, g)} />
                  ) : (
                    <input type="number" min="0" step="1" value={x.amount_grams ?? ''} onChange={e => setAmount(x.key, e.target.value ? Number(e.target.value) : null)}
                      style={inputStyle} placeholder="Aantal gram" />
                  )}
                  {x.quantityNote && x.amount_grams > 0 && (() => {
                    const note = x.quantityNote;
                    const converted = note.state === 'raw' ? x.amount_grams * note.factor : x.amount_grams / note.factor;
                    const convertedRounded = Math.round(converted);
                    const otherStateLabel = note.state === 'raw' ? 'gekookte/gebakken' : 'rauwe';
                    return (
                      <div style={{ marginTop: 6, padding: '6px 8px', borderRadius: 6, background: 'rgba(245,158,11,0.1)', border: '1px solid #f59e0b' }}>
                        <div style={{ fontSize: 11, color: '#b45309', display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                          <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 1 }} />
                          <span>
                            Dit is de {note.state === 'raw' ? 'rauwe' : 'gekookte/gebakken'} variant van {note.category} (factor {note.factor}x rauw&rarr;gekookt).
                            Bedoelde je de {otherStateLabel} hoeveelheid? Dat zou ~{convertedRounded} g zijn.
                          </span>
                        </div>
                        {note.paired_food ? (
                          <button type="button" onClick={() => applyQuantityConversion(x.key, note, converted)} style={{ ...ghostBtn, marginTop: 6 }}>
                            Gebruik {note.paired_food.name} · {convertedRounded} g
                          </button>
                        ) : (
                          <div style={{ fontSize: 10, color: 'var(--text-dimmed)', marginTop: 4 }}>
                            Geen exacte NEVO-match gevonden voor de {otherStateLabel} variant — zoek deze zelf op als je dat bedoelde.
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  <div style={{ marginTop: 6 }}>
                    <select value={x.role} onChange={e => setRole(x.key, e.target.value)} style={{ ...inputStyle, padding: '5px 8px', fontSize: 12 }}>
                      {ROLE_OPTIONS.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
                    </select>
                  </div>
                  {(!x.food || openCorrection === x.key) && (
                    <div style={{ marginTop: 6 }}>
                      <FoodAutocomplete onPick={food => replaceFood(x.key, food)} placeholder="Zoek het juiste NEVO-item…" />
                      {/* Same escape hatch as "Extra ingrediënt toevoegen" below (AddFoodModal via
                          addFoodTarget) — available whenever this row's search box is open, whether
                          that's because it was never matched, or because the chef clicked "Wijzig" on
                          an existing match that turned out to be wrong. Either way replaceFood(x.key, …)
                          already handles swapping this specific row's food, unmatched or not. */}
                      <button type="button" onClick={() => setAddFoodTarget(x.key)} style={{ ...ghostBtn, marginTop: 6 }}>
                        {x.food ? 'Toch niet de juiste match? Barcode scannen, foto of handmatig invullen…' : 'Niet gevonden? Barcode scannen of handmatig toevoegen…'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
            <div style={{ marginTop: 8 }}>
              <span style={{ ...label, marginBottom: 3 }}>Extra ingrediënt toevoegen</span>
              <FoodAutocomplete onPick={addIngredient} placeholder="Add ingredient…" />
              <button type="button" onClick={() => setAddFoodTarget('new')} style={{ ...ghostBtn, marginTop: 6 }}>
                Product niet gevonden? Nieuw product toevoegen…
              </button>
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            <span style={label}>Preparation</span>
            <textarea value={preparation} onChange={e => setPreparation(e.target.value)}
              style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} placeholder="Bereidingswijze…" />
          </div>

          <div style={{ marginTop: 14 }}>
            <span style={label}>Materialen</span>
            <textarea value={materials} onChange={e => setMaterials(e.target.value)}
              style={{ ...inputStyle, minHeight: 50, resize: 'vertical' }} placeholder="Benodigde materialen/apparatuur…" />
          </div>

          <div style={{ marginTop: 14 }}>
            <span style={label}>Opmerkingen/Allergenen</span>
            <textarea value={notesAllergens} onChange={e => setNotesAllergens(e.target.value)}
              style={{ ...inputStyle, minHeight: 50, resize: 'vertical' }} placeholder="Opmerkingen, allergenen…" />
          </div>

          {error && <div style={{ fontSize: 12, color: '#ef4444', marginTop: 10 }}>{error}</div>}

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button style={btn(true)} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save recipe'}</button>
            <button style={btn(false)} onClick={onCancel}>Cancel</button>
          </div>
        </div>
      </div>
      {addFoodTarget && (
        <AddFoodModal
          onClose={() => setAddFoodTarget(null)}
          onAdded={(food) => {
            if (addFoodTarget === 'new') addIngredient(food);
            else replaceFood(addFoodTarget, food);
            setAddFoodTarget(null);
          }}
          // "In een rits" rapid multi-add: every extra product added this way always becomes a
          // new ingredient row (there's no "next specific unmatched row" to target once the modal
          // was opened to fix one particular row) — the modal itself stays open for the next one,
          // so setAddFoodTarget is deliberately NOT reset to null here.
          onAddedKeepOpen={(food) => addIngredient(food)}
        />
      )}
      {roleWarnings && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 350, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => onSaved(roleWarnings.saved)}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-card)', border: '1px solid #f59e0b', borderRadius: 16, width: '100%', maxWidth: 380, padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 700, marginBottom: 10 }}>
              <AlertTriangle size={16} color="#f59e0b" /> Rol komt niet overeen
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginBottom: 10 }}>
              Het recept is opgeslagen. Deze ingrediënt-rol(len) komen niet overeen met de indeling in de database — corrigeer de rol als dat klopt.
            </div>
            {roleWarnings.warnings.map((w, i) => (
              <div key={i} style={{ fontSize: 12, color: '#f59e0b', marginBottom: 6 }}>{w.message}</div>
            ))}
            <button style={{ ...btn(true, '#f59e0b'), marginTop: 8 }} onClick={() => onSaved(roleWarnings.saved)}>Begrepen</button>
          </div>
        </div>
      )}
    </div>
  );
}
